/**
 * Recording a registrant's consent to a registrant change (domain actions), with the rules of
 * `subsystems/registrar/facade/adapters/admin-api/src/admin/domain_actions.rs` (client-api spec F11, review #7; owner
 * decision 2026-10-05 §6):
 *
 * - Tino staff record how consent was obtained, by any method;
 * - anyone else (a customer user) records nothing: each registrant confirms with the link
 *   e-mailed to them (the server refuses the step).
 *
 * Pure functions (no React): `npm test` covers them.
 */

import type { ConsentMethod } from '../api/areas/core';

/** The two parties, as the server spells them. */
export const CONSENT_SIDES = ['CURRENT_REGISTRANT', 'NEW_REGISTRANT'] as const;
export type ConsentSide = (typeof CONSENT_SIDES)[number];

/** How staff may have obtained a side's consent. */
export const CONSENT_METHODS = ['IN_PERSON', 'SIGNED_DOCUMENT', 'DIGITAL_SIGNATURE', 'EMAIL_LINK', 'PHONE_CALL'] as const satisfies readonly ConsentMethod[];
export type StaffConsentMethod = (typeof CONSENT_METHODS)[number];

/** Who records a side's consent ([`consentMode`]). */
export type ConsentMode = 'STAFF' | 'EMAIL_LINK';

/** A `/me` grant (`scope` is the server's rendering: `Global`, `Customer(CustomerId(<id>))`, …). */
export interface ConsentGrant {
  scope: string;
  permissions: readonly string[];
}

/**
 * The server's rule for the signed-in user: `STAFF` with a platform-wide `domain.write`,
 * otherwise `EMAIL_LINK`. `grants` not loaded (`null`): `STAFF`, and the server decides.
 */
export function consentMode(grants: readonly ConsentGrant[] | null): ConsentMode {
  if (!grants) return 'STAFF';
  return grants.some((g) => g.scope === 'Global' && g.permissions.includes('domain.write')) ? 'STAFF' : 'EMAIL_LINK';
}
